'use client';
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const GAP = 8;
const ARROW = 4;
const TIP_BG = '#1f2937'; // gray-800
const MARGIN = 8; // khoảng cách tối thiểu tới mép viewport (ngang lẫn dọc)

type TipDir = 'up' | 'down';
type TipAlign = 'center' | 'start' | 'end';

interface BoxPos { left?: number; right?: number; top?: number; bottom?: number; transform?: string; }

function horizAnchor(rect: DOMRect, align: TipAlign): BoxPos {
  if (align === 'start') return { left: rect.left };
  if (align === 'end') return { left: rect.right, transform: 'translateX(-100%)' };
  return { left: rect.left + rect.width / 2, transform: 'translateX(-50%)' };
}

// Vị trí hộp tooltip — tính bằng toạ độ viewport (getBoundingClientRect) vì render qua portal
// (position:fixed ở document.body), KHÔNG còn phụ thuộc containing block của wrapper nữa.
function boxPos(rect: DOMRect, dir: TipDir, align: TipAlign): BoxPos {
  const horiz = horizAnchor(rect, align);
  if (dir === 'down') return { ...horiz, top: rect.bottom + GAP };
  return { ...horiz, top: rect.top - GAP, transform: `${horiz.transform ?? ''} translateY(-100%)`.trim() };
}

function arrowHoriz(align: TipAlign): CSSProperties {
  if (align === 'start') return { left: 16 };
  if (align === 'end') return { right: 16 };
  return { left: '50%', transform: 'translateX(-50%)' };
}

function arrowPos(dir: TipDir, align: TipAlign): CSSProperties {
  if (dir === 'down') return { top: -ARROW * 2, ...arrowHoriz(align) };
  return { bottom: -ARROW * 2, ...arrowHoriz(align) };
}

// Tam giác mũi tên trỏ NGƯỢC hướng tooltip về phía trigger (dir='down' → tooltip nằm dưới trigger →
// mũi tên trỏ LÊN, tức viền TRÊN của hộp tam giác tô màu).
function arrowBorderColor(dir: TipDir): string {
  if (dir === 'down') return `transparent transparent ${TIP_BG} transparent`;
  return `${TIP_BG} transparent transparent transparent`;
}

// Số px cần dịch hộp để không tràn viewport theo trục NGANG — 0 nếu đã nằm trong bounds.
function clampShiftX(box: DOMRect): number {
  if (box.left < MARGIN) return MARGIN - box.left;
  if (box.right > window.innerWidth - MARGIN) return (window.innerWidth - MARGIN) - box.right;
  return 0;
}

// Mũi tên dịch NGƯỢC lại khi hộp bị kẹp ngang — vẫn chỉ đúng vào trigger dù hộp đã lệch khỏi vị trí
// lý tưởng. translateX cộng dồn được với transform gốc (nếu có) qua nối chuỗi.
function shiftedArrowStyle(arrow: CSSProperties, shiftX: number): CSSProperties {
  if (shiftX === 0) return arrow;
  return { ...arrow, transform: `${arrow.transform ?? ''} translateX(${-shiftX}px)`.trim() };
}

/**
 * Tooltip hover dùng chung cho toàn GMS — HIỆN NGAY (không trễ như native title=""), style được.
 *
 * Trước đây dùng CSS group-hover thuần (position:absolute trong wrapper `relative`) — bị CẮT MẤT
 * khi wrapper nằm trong ancestor overflow-x-auto/overflow-hidden (bảng cuộn ngang, sticky header…),
 * vì tooltip tràn ra ngoài vùng nhìn thấy của ancestor bị clip. Tái diễn ở nhiều trang (reports,
 * timeline, pricing…) trước khi sửa TẬN GỐC ở đây (2026-08-06, theo cách đã áp dụng cho dự án fsap
 * — packages/ui/src/components/Tip.tsx): đổi hẳn sang portal (position:fixed + document.body), tính
 * toạ độ bằng getBoundingClientRect() lúc hover. Tip LUÔN nổi trên MỌI ancestor bất kể overflow gì —
 * không cần nhớ "chỗ nào an toàn dùng Tip, chỗ nào phải tự viết tooltip riêng" như trước.
 *
 * Kẹp trong viewport (2026-08-19, theo fsap `feedback_tip_tooltip_pattern` §viewport-clamp): sau khi
 * render lần đầu theo `dir` yêu cầu, đo lại bounding box — nếu trigger sát mép TRÊN màn hình (`dir='up'`
 * không đủ chỗ bung lên, hộp tràn âm `top` → bị cắt, đúng bug "Chat Airbnb" ở header) hoặc sát mép DƯỚI
 * (`dir='down'` tràn quá `innerHeight`) thì TỰ LẬT sang hướng ngược lại — không cần đổi `dir` ở từng
 * call site. fsap mới kẹp được trục NGANG (trigger sát mép trái/phải); trục DỌC (lật up↔down) là phần
 * GMS thêm ở đây vì bug thực tế lộ ra ở trục này trước. Kẹp ngang (trái/phải) làm sau cùng, dịch cả
 * hộp lẫn mũi tên để mũi tên vẫn trỏ đúng vào trigger dù hộp đã lệch khỏi vị trí lý tưởng.
 *
 * Cạm bẫy còn lại: vị trí chỉ tính lại lúc mouseEnter (không theo dõi scroll/resize khi đang mở) —
 * chấp nhận được vì tooltip ẩn ngay khi rời chuột; nếu cần tooltip đứng yên khi cuộn (vd dropdown),
 * xem pattern đầy đủ hơn ở `app/(app)/timeline/page.tsx` (autoTip, có thêm scroll/resize listener).
 */
export default function Tip({
  text,
  children,
  wide,
  dir = 'up',
  align = 'center',
  block,
  className,
}: Readonly<{
  text: ReactNode;
  children: ReactNode;
  /** nới rộng tooltip (260px thay vì 210px) cho nội dung dài */
  wide?: boolean;
  /** hướng bung: 'up' (mặc định) hoặc 'down' — dùng 'down' trong bảng cuộn. Tự lật nếu sát mép trên/dưới màn hình. */
  dir?: TipDir;
  /** canh ngang: 'center' (mặc định) · 'start' (mép trái theo trigger) · 'end' (mép phải).
   *  Dùng 'start'/'end' cho trigger sát mép màn hình để tooltip không tràn ra ngoài & bị cắt. */
  align?: TipAlign;
  /** wrapper block w-full (cho input/select cần chiếm trọn bề rộng) thay vì inline-block */
  block?: boolean;
  /** class thêm cho span bao ngoài (vd điều chỉnh align) */
  className?: string;
}>) {
  const ref = useRef<HTMLSpanElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const flippedRef = useRef(false);
  const [pos, setPos] = useState<BoxPos | null>(null);
  const [effectiveDir, setEffectiveDir] = useState<TipDir>(dir);
  const [shiftX, setShiftX] = useState(0);

  useLayoutEffect(() => {
    const el = boxRef.current;
    const triggerRect = ref.current?.getBoundingClientRect();
    if (!pos || !el || !triggerRect) return;
    const box = el.getBoundingClientRect();
    if (!flippedRef.current) {
      const flipToDown = effectiveDir === 'up' && box.top < MARGIN;
      const flipToUp = effectiveDir === 'down' && box.bottom > window.innerHeight - MARGIN;
      if (flipToDown || flipToUp) {
        flippedRef.current = true;
        const nextDir: TipDir = flipToDown ? 'down' : 'up';
        setEffectiveDir(nextDir);
        setPos(boxPos(triggerRect, nextDir, align));
        return;
      }
    }
    const shift = clampShiftX(box);
    if (shift === 0) return;
    setPos(p => (p ? { ...p, left: (p.left ?? 0) + shift } : p));
    setShiftX(s => s + shift);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, effectiveDir]);

  if (!text && text !== 0) return <>{children}</>;
  const onEnter = () => {
    const rect = ref.current?.getBoundingClientRect();
    if (rect) {
      flippedRef.current = false;
      setShiftX(0);
      setEffectiveDir(dir);
      setPos(boxPos(rect, dir, align));
    }
  };
  return (
    <span
      ref={ref}
      className={`${block ? 'block w-full' : 'inline-block align-bottom'} ${className ?? ''}`}
      onMouseEnter={onEnter}
      onMouseLeave={() => setPos(null)}
    >
      {children}
      {pos && createPortal(
        <div
          ref={boxRef}
          // Bề rộng: `w-max` nên tooltip chỉ rộng bằng nội dung — nới `max-w` KHÔNG làm tip ngắn to ra,
          // chỉ giúp tip DÀI bớt xuống dòng vụn (210px ở text-xs chỉ ~30 ký tự/dòng, nội dung 3 ý gãy
          // thành cả chục dòng, đọc rất mệt — user phản ánh 2026-09-05). Kẹp theo viewport để không tràn
          // trên máy hẹp.
          style={{ ...pos, maxWidth: `min(${wide ? 380 : 280}px, calc(100vw - 24px))` }}
          className="fixed w-max text-xs bg-gray-800 text-white rounded-lg px-2.5 py-1.5 z-[9999] text-left pointer-events-none leading-snug whitespace-normal font-normal"
        >
          {text}
          <span
            className="absolute"
            style={{ ...shiftedArrowStyle(arrowPos(effectiveDir, align), shiftX), width: 0, height: 0, borderWidth: ARROW, borderStyle: 'solid', borderColor: arrowBorderColor(effectiveDir) }}
          />
        </div>,
        document.body,
      )}
    </span>
  );
}
